import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';

import { useDeprecatedLocalStorage, generateUserSpecificPersistentKey } from '../../../hooks/use-local-storage';
import Global from '../../../store/global-selectors';

export const PurchaseOrderSectionId = {
  ShippingDetails: 'ShippingDetails',
  PaymentDetails: 'PaymentDetails',
};

const defaultExpandedSections = {
  [PurchaseOrderSectionId.ShippingDetails]: true,
  [PurchaseOrderSectionId.PaymentDetails]: true,
};

const USER_PURCHASE_ORDER_EXPANDED_SECTION_STATE = 'purchaseOrder/expandedSections';

const PurchaseOrderExpandedSectionContext = createContext({
  expandedSections: {},
  updateExpandedSections: noop,
});

const usePurchaseOrderExpandedSections = () => useContext(PurchaseOrderExpandedSectionContext);

export const usePurchaseOrderExpanderSection = (sectionId) => {
  const { expandedSections, updateExpandedSections } = usePurchaseOrderExpandedSections();

  const toggleIsExpanded = useCallback(() => {
    updateExpandedSections((prevState) => {
      return {
        ...prevState,
        [sectionId]: !prevState?.[sectionId],
      };
    });
  }, [sectionId, updateExpandedSections]);

  return { isExpanded: expandedSections[sectionId], toggleIsExpanded };
};

export const PurchaseOrderExpandedSectionProvider = ({ children }) => {
  const userId = useSelector(Global.selectUserCurrentUserId);

  const [purchaseOrderExpandedSections, setPurchaseOrderExpandedSections] = useDeprecatedLocalStorage(
    generateUserSpecificPersistentKey(USER_PURCHASE_ORDER_EXPANDED_SECTION_STATE, userId),
    defaultExpandedSections,
  );

  const updateExpandedSections = useCallback(
    (newValue) => {
      setPurchaseOrderExpandedSections(newValue);
    },
    [setPurchaseOrderExpandedSections],
  );

  const value = useMemo(() => {
    return {
      expandedSections: purchaseOrderExpandedSections,
      updateExpandedSections,
    };
  }, [purchaseOrderExpandedSections, updateExpandedSections]);

  return <PurchaseOrderExpandedSectionContext.Provider value={value}>{children}</PurchaseOrderExpandedSectionContext.Provider>;
};

export default PurchaseOrderExpandedSectionProvider;
